<div class="px-2 py-3">
  <!-- Header -->
  <div class="flex items-start gap-3">
    <span class="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400">
      <lucide-icon [name]="CalendarCheckIcon" class="h-5 w-5"></lucide-icon>
    </span>
    <div>
      <h2 class="text-lg font-semibold text-gray-800 dark:text-white/90">Viewings</h2>
      <p class="text-sm text-gray-500 dark:text-gray-400">
        Every viewing booked, from today onwards. Each line is one booking — open it to go to the contact.
      </p>
    </div>
  </div>

  <!-- Filters -->
  <div class="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
    <app-text-input
      label="Search"
      name="search"
      placeholder="Contact name or venue"
      [control]="searchControl"
      [clearable]="true"
    ></app-text-input>

    <app-select
      label="Venue"
      placeholder="All venues"
      [control]="venueControl"
      [choices]="venueChoices"
      [searchable]="true"
      [clearable]="true"
    ></app-select>
  </div>

  <!-- Count -->
  @if (hasLoadedOnce && !loadError && showRounds.length) {
    <div class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      Showing {{ showRounds.length }} of {{ total }} {{ total === 1 ? 'viewing' : 'viewings' }}
    </div>
  }

  <!-- Error -->
  @if (loadError) {
    <div class="mt-4 flex items-start justify-between gap-3 rounded-xl border border-red-200 bg-red-50/60 p-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/[0.06] dark:text-red-300">
      <span>{{ loadError }}</span>
      <button type="button" (click)="retry()" class="shrink-0 font-medium underline">Try again</button>
    </div>
  }

  <!-- Empty state -->
  @if (isEmpty && !loadError) {
    <div class="mt-4 rounded-xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-500 dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-400">
      @if (isFiltered) {
        No viewings match that search.
      } @else {
        No viewings are booked from today onwards.
      }
    </div>
  }

  <!-- Rows: one line per booking -->
  @if (showRounds.length) {
    <div class="mt-4 overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
      <!-- Column headings, on wide screens only; each row reads as a line on its own below that. -->
      <div class="hidden border-b border-gray-100 px-4 py-2 text-xs font-medium text-gray-500 sm:grid sm:grid-cols-12 sm:gap-4 dark:border-gray-800 dark:text-gray-400">
        <div class="sm:col-span-4">Contact</div>
        <div class="sm:col-span-3">Venue</div>
        <div class="sm:col-span-3">Date</div>
        <div class="sm:col-span-2">Status</div>
      </div>

      @for (showRound of showRounds; track showRound.event_id) {
        <a
          [routerLink]="showRound.contact ? ['/contacts', showRound.contact.id] : null"
          class="block border-b border-gray-100 px-4 py-3 transition last:border-b-0 hover:bg-gray-50 sm:grid sm:grid-cols-12 sm:items-center sm:gap-4 dark:border-gray-800 dark:hover:bg-white/[0.06]"
        >
          <div class="sm:col-span-4">
            <div class="text-sm font-medium text-gray-800 dark:text-white/90">
              {{ showRound.contact?.name ?? 'No contact linked' }}
            </div>
            @if (showRound.event_reference) {
              <div class="text-xs text-gray-500 dark:text-gray-400">{{ showRound.event_reference }}</div>
            }
          </div>

          <div class="mt-1 text-sm text-gray-600 sm:col-span-3 sm:mt-0 dark:text-gray-300">
            {{ showRound.venue?.name ?? '—' }}
          </div>

          <div class="mt-1 text-sm text-gray-600 sm:col-span-3 sm:mt-0 dark:text-gray-300">
            {{ showRound.date }}
            @if (showRound.time) {
              <span class="text-gray-500 dark:text-gray-400">at {{ showRound.time }}</span>
            }
          </div>

          <div class="mt-2 sm:col-span-2 sm:mt-0">
            <span
              class="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium"
              [ngClass]="
                showRound.status === 'booked'
                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300'
                  : 'bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-white/70'
              "
            >
              {{ showRound.status_label }}
            </span>
          </div>

          <!-- TVG-339: what the customer said after this viewing. On its own
               line beneath the booking, because three answers and a comment do
               not fit a column and the comment is the part worth reading. -->
          @if (showRound.review; as review) {
            <div class="mt-2 sm:col-span-12 sm:mt-1" data-testid="show-round-review">
              <div class="flex flex-wrap gap-1.5">
                <span
                  class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                  [ngClass]="reviewClasses(review.viewing_rating)"
                  title="How was your viewing?"
                >
                  Viewing: {{ review.viewing_rating_label }}
                </span>
                <span
                  class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                  [ngClass]="reviewClasses(review.venue_rating)"
                  title="Did you like the venue?"
                >
                  Venue: {{ review.venue_rating_label }}
                </span>
                <span
                  class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                  [ngClass]="reviewClasses(review.getting_married)"
                  title="Can you see yourself getting married here?"
                >
                  Getting married: {{ review.getting_married_label }}
                </span>
              </div>
              @if (review.feedback) {
                <p class="mt-1.5 whitespace-pre-line text-xs text-gray-600 dark:text-gray-400">
                  “{{ review.feedback }}”
                </p>
              }
            </div>
          }
        </a>
      }
    </div>
  }

  <!-- Scroll sentinel: fetches the next page as it comes into view. -->
  <div #scrollSentinel class="h-px"></div>

  @if (loading) {
    <div class="mt-3 text-center text-sm text-gray-500 dark:text-gray-400">Loading…</div>
  }

  @if (!loading && !hasMore && showRounds.length) {
    <div class="mt-3 text-center text-xs text-gray-400 dark:text-gray-500">That's every viewing.</div>
  }
</div>
